import axiosInstance from './api'
import { getIso2FromCountryName } from 'shared/utils/countryNameByIso2'
import {
	fixtureFetchStudentProfile,
	shouldUseApplicationFlowFixtures,
} from 'fixtures/applicationFlowFixtures'

/**
 * Fetch organization students (all students in counselor's organization)
 */
export const fetchOrganizationStudents = async () => {
	try {
		const response = await axiosInstance.get(
			'/counseling/organization/students',
		)
		return response.data
	} catch (error) {
		console.error('Error fetching organization students:', error)
		throw error
	}
}

/**
 * Fetch assigned students (students assigned to current counselor)
 */
export const fetchAssignedStudents = async () => {
	try {
		const response = await axiosInstance.get('/counseling/counselor/students')
		return response.data
	} catch (error) {
		console.error('Error fetching assigned students:', error)
		throw error
	}
}

/**
 * Fetch profile builder data for all organization students
 */
export const fetchOrganizationStudentsProfileBuilder = async () => {
	try {
		const response = await axiosInstance.get(
			'/counseling/organization/students/profile-builder',
		)
		return response.data
	} catch (error) {
		console.error(
			'Error fetching organization students profile builder:',
			error,
		)
		throw error
	}
}

/**
 * Fetch student profile_builder data by student_id
 */
export const fetchStudentProfile = async studentId => {
	try {
		// Normalize studentId before making the request
		const normalizedId = String(studentId || '').trim()
		if (!normalizedId) {
			throw new Error('Invalid studentId: empty or null')
		}

		if (shouldUseApplicationFlowFixtures()) {
			return fixtureFetchStudentProfile(normalizedId)
		}

		// Encode the ID for URL (handles spaces and special characters)
		const encodedId = encodeURIComponent(normalizedId)
		const url = `/counseling/counselor/students/${encodedId}/profile`

		console.log('Fetching profile for studentId:', {
			original: studentId,
			normalized: normalizedId,
			encoded: encodedId,
			url,
		})

		const response = await axiosInstance.get(url)
		console.log('fetchStudentProfile response:', response.data)

		return response.data
	} catch (error) {
		console.error('Error fetching student profile:', error)
		throw error
	}
}

/**
 * Fetch saved student lists for current counselor
 */
export const fetchStudentLists = async () => {
	try {
		const response = await axiosInstance.get(
			'/counseling/counselor/student-lists',
		)
		return response.data
	} catch (error) {
		console.error('Error fetching student lists:', error)
		throw error
	}
}

/**
 * Create a new saved student list
 */
export const createStudentList = async (name, studentIds) => {
	try {
		const response = await axiosInstance.post(
			'/counseling/counselor/student-lists',
			{
				name,
				studentIds,
			},
		)
		return response.data
	} catch (error) {
		console.error('Error creating student list:', error)
		throw error
	}
}

/**
 * Update an existing student list
 */
export const updateStudentList = async (listId, updates) => {
	try {
		const response = await axiosInstance.put(
			`/counseling/counselor/student-lists/${encodeURIComponent(listId)}`,
			updates,
		)
		return response.data
	} catch (error) {
		console.error('Error updating student list:', error)
		throw error
	}
}

/**
 * Delete a student list
 */
export const deleteStudentList = async listId => {
	try {
		const response = await axiosInstance.delete(
			`/counseling/counselor/student-lists/${encodeURIComponent(listId)}`,
		)
		return response.data
	} catch (error) {
		console.error('Error deleting student list:', error)
		throw error
	}
}

/**
 * Fetch student career assessment results (quiz results including personality type)
 */
export const fetchStudentCareerAssessment = async studentId => {
	try {
		const normalizedId = String(studentId || '').trim()
		if (!normalizedId) {
			throw new Error('Invalid studentId: empty or null')
		}

		const encodedId = encodeURIComponent(normalizedId)
		const response = await axiosInstance.get(
			`/counseling/counselor/students/${encodedId}/career-assessment`,
		)
		return response.data
	} catch (error) {
		console.error('Error fetching student career assessment:', error)
		throw error
	}
}

/**
 * Map backend student data to frontend format
 */
export const mapBackendStudentToFrontend = (backendStudent, index) => {
	// First target country, else dream_country; no invented default when both missing.
	const rawCountry =
		backendStudent.target_countries?.[0] || backendStudent.dream_country || null

	const countryIso2 = rawCountry ? getIso2FromCountryName(rawCountry) : null

	// Normalize student_id - remove whitespace and ensure it's a valid string
	const normalizedId = backendStudent.student_id
		? String(backendStudent.student_id).trim()
		: String(index || '').trim()

	// Debug: Log student_id format
	if (index === 0) {
		console.log('[DEBUG] mapBackendStudentToFrontend: First student', {
			backendStudent_id: backendStudent.student_id,
			normalizedId,
			type: typeof backendStudent.student_id,
			length: normalizedId.length,
			avatarKey: backendStudent.avatarKey || backendStudent.avatar_key || null,
			avatarSource:
				backendStudent.avatarSource || backendStudent.avatar_source || null,
			photoUrl: backendStudent.photoUrl || backendStudent.photourl || '',
		})
	}

	// Extract grade number from "10 Grade" format or use default
	const extractGrade = gradeStr => {
		if (!gradeStr) return 12 // Default
		// Extract number from strings like "10 Grade", "11 Grade", "12 Grade"
		const match = String(gradeStr).match(/(\d+)/)
		return match ? parseInt(match[1], 10) : 12
	}

	const gradeNumber = extractGrade(backendStudent.grade)
	const gradeLabel = backendStudent.grade
		? String(backendStudent.grade).trim()
		: String(gradeNumber)

	const kandorPlan = String(backendStudent.kandor_plan || 'free')
		.trim()
		.toLowerCase()
	// Interim UI labels until product finalizes Essential/Premium mapping.
	const planLabel = kandorPlan === 'premium' ? 'Premium' : 'Essential'
	const assignmentsCount = Number(backendStudent.assignments_count) || 0
	const counselorId = backendStudent.counselor_id
		? String(backendStudent.counselor_id).trim()
		: null

	return {
		id: normalizedId,
		name: backendStudent.name || 'Unknown',
		grade: gradeNumber, // numeric for Students page tabs/filters
		gradeLabel, // raw org grade string for User Management
		status: 'needsHelp', // Default - you may need to calculate this
		tag: 'Needs Help', // Default
		documentsMissing: 0, // Default - you may need to add this to backend
		lastActive: backendStudent.joined_date
			? new Date(backendStudent.joined_date).toLocaleDateString()
			: 'Unknown',
		country: countryIso2,
		course: backendStudent.category || backendStudent.sub_category || 'N/A',
		counselor: backendStudent.counselor_name || 'Current Counselor',
		counselorId,
		kandorPlan,
		plan: planLabel,
		assignmentsCount,
		assignments: assignmentsCount,
		career: backendStudent.career || backendStudent.category || 'N/A',
		readiness: 0, // Default - you may need to add this to backend
		// Additional backend fields
		email: backendStudent.email,
		phone: backendStudent.phone,
		photoUrl: backendStudent.photoUrl || '',
		avatarKey: backendStudent.avatarKey || null,
		avatarSource: backendStudent.avatarSource || null,
		target_countries: backendStudent.target_countries || [],
		dream_country: backendStudent.dream_country,
		sub_category: backendStudent.sub_category,
		city: backendStudent.city,
		intake: backendStudent.intake,
		funds: backendStudent.funds,
		shortlist_count: backendStudent.shortlist_count || 0,
		joined_date: backendStudent.joined_date,
		disabled: Boolean(
			backendStudent.disabled ??
			backendStudent.is_disabled ??
			backendStudent.account_status === 'disabled'
		),
	}
}

export const mockStudents = [
	{
		id: 1,
		name: 'Sarah Chen',
		grade: 12,
		status: 'needsHelp',
		tag: 'Needs Help',
		documentsMissing: 2,
		lastActive: '2 hours ago',

		country: 'US',
		course: 'Physics, Math, CS',
		counselor: 'Dr. Martinez',
		career: 'Engineering',
		readiness: 85,
	},
	{
		id: 2,
		name: 'Marcus Johnson',
		grade: 12,
		status: 'needsHelp',
		tag: 'Needs Help',
		documentsMissing: 0,
		lastActive: '2 hours ago',
		country: 'AU',
		course: 'Biology, Chemistry',
		counselor: 'Ms. Thompson',
		career: 'Medicine',
		readiness: 45,
	},
	{
		id: 3,
		name: 'Emma Williams',
		grade: 11,
		status: 'needsHelp',
		tag: 'Needs Help',
		documentsMissing: 5,
		lastActive: '2 hours ago',

		country: 'DE',
		course: 'English, History',
		counselor: 'Dr. Martinez',
		career: 'Business',
		readiness: 72,
	},
	{
		id: 4,
		name: 'James Park',
		grade: 12,
		status: 'needsHelp',
		tag: 'Needs Help',
		documentsMissing: 8,
		lastActive: '2 hours ago',

		country: 'DE',
		course: 'CS, Math',
		counselor: 'Ms. Thompson',
		career: 'Computer Science',
		readiness: 68,
	},
	{
		id: 5,
		name: 'Olivia Brown',
		grade: 12,
		status: 'needsHelp',
		tag: 'Needs Help',
		documentsMissing: 0,
		lastActive: '2 hours ago',

		country: 'AU',
		course: 'Art, Design',
		counselor: 'Dr. Martinez',
		career: 'Design',
		readiness: 90,
	},
	{
		id: 6,
		name: 'David Lee',
		grade: 11,
		status: 'needsHelp',
		tag: 'Needs Help',
		documentsMissing: 12,
		lastActive: '2 hours ago',

		country: 'AU',
		course: 'Chemistry, Biology',
		counselor: 'Ms. Thompson',
		career: 'Medicine',
		readiness: 40,
	},
	{
		id: 7,
		name: 'Sarah Chen',
		grade: 12,
		status: 'needsHelp',
		tag: 'Needs Help',
		documentsMissing: 2,
		lastActive: '2 hours ago',

		country: 'US',
		course: 'Physics, Math, CS',
		counselor: 'Dr. Martinez',
		career: 'Engineering',
		readiness: 85,
	},
	{
		id: 8,
		name: 'Marcus Johnson',
		grade: 12,
		status: 'needsHelp',
		tag: 'Needs Help',
		documentsMissing: 0,
		lastActive: '2 hours ago',

		country: 'AU',
		course: 'Biology, Chemistry',
		counselor: 'Ms. Thompson',
		career: 'Medicine',
		readiness: 45,
	},
	{
		id: 9,
		name: 'Emma Williams',
		grade: 11,
		status: 'needsHelp',
		tag: 'Needs Help',
		documentsMissing: 5,
		lastActive: '2 hours ago',

		country: 'DE',
		course: 'English, History',
		counselor: 'Dr. Martinez',
		career: 'Business',
		readiness: 72,
	},
	{
		id: 10,
		name: 'James Park',
		grade: 12,
		status: 'needsHelp',
		tag: 'Needs Help',
		documentsMissing: 8,
		lastActive: '2 hours ago',

		country: 'DE',
		course: 'CS, Math',
		counselor: 'Ms. Thompson',
		career: 'Computer Science',
		readiness: 68,
	},
	{
		id: 11,
		name: 'Olivia Brown',
		grade: 12,
		status: 'needsHelp',
		tag: 'Needs Help',
		documentsMissing: 0,
		lastActive: '2 hours ago',

		country: 'AU',
		course: 'Art, Design',
		counselor: 'Dr. Martinez',
		career: 'Design',
		readiness: 90,
	},
	{
		id: 12,
		name: 'David Lee',
		grade: 11,
		status: 'needsHelp',
		tag: 'Needs Help',
		documentsMissing: 12,
		lastActive: '2 hours ago',

		country: 'AU',
		course: 'Chemistry, Biology',
		counselor: 'Ms. Thompson',
		career: 'Medicine',
		readiness: 40,
	},
]
